/**
 * EMR Template Engine Utility
 *
 * Provides Mustache-style template substitution for API paths and request bodies.
 * Supports simple variable replacement and nested property access.
 *
 * Single Responsibility: Template substitution
 *
 * Examples:
 * - applyTemplate("/api/doctor/recommendations/{{recommendationId}}/items/{{itemId}}/action",
 *                 { recommendationId: "123", itemId: "456" })
 *   → "/api/doctor/recommendations/123/items/456/action"
 *
 * - applyTemplate({ action: "{{actionType}}", note: "{{noteOrReason}}" },
 *                 { actionType: "ADD", noteOrReason: "Looks good" })
 *   → { action: "ADD", note: "Looks good" }
 */

/**
 * Apply template substitution to a string, object, or array
 * @param {string|Object|Array} template - Template to process
 * @param {Object} context - Context object with variable values
 * @returns {string|Object|Array} - Processed template
 */
export function applyTemplate(template, context = {}) {
  if (template == null) {
    return template
  }

  // Handle string templates
  if (typeof template === 'string') {
    return applyStringTemplate(template, context)
  }

  // Handle array templates
  if (Array.isArray(template)) {
    return template.map(item => applyTemplate(item, context))
  }

  // Handle object templates
  if (typeof template === 'object') {
    return applyObjectTemplate(template, context)
  }

  // Return primitives as-is
  return template
}

/**
 * Apply template substitution to a string
 * Replaces {{variable}} or {{nested.property}} with values from context
 */
function applyStringTemplate(template, context) {
  const pattern = /\{\{([^}]+)\}\}/g

  return template.replace(pattern, (match, expression) => {
    const value = getNestedValue(expression.trim(), context)
    return value != null ? String(value) : match // Keep placeholder if value not found
  })
}

/**
 * Apply template substitution to an object
 * Recursively processes all values
 */
function applyObjectTemplate(template, context) {
  const result = {}

  for (const [key, value] of Object.entries(template)) {
    result[key] = applyTemplate(value, context)
  }

  return result
}

/**
 * Get nested value from context using dot notation
 * Examples:
 * - "recommendationId" → context.recommendationId
 * - "item.state" → context.item.state
 * - "item.values.fieldCode" → context.item.values.fieldCode
 */
function getNestedValue(path, context) {
  const parts = path.split('.')
  let value = context

  for (const part of parts) {
    if (value == null) {
      return undefined
    }
    value = value[part]
  }

  return value
}

/**
 * Build context object from runtime and item data
 * Helper function to create a complete context for template substitution
 *
 * @param {Object} options - Context building options
 * @param {Object} options.runtime - Runtime context (recommendationId, patientId, etc.)
 * @param {Object} options.item - Item data (for item-level actions)
 * @param {Object} options.sectionData - Section data (for section-level actions)
 * @param {Object} options.formData - Form data from dialog (for dialog actions)
 * @returns {Object} - Complete context object
 */
export function buildTemplateContext({
  runtime = {},
  item = null,
  sectionData = null,
  formData = {}
}) {
  return {
    ...runtime,
    ...formData,
    item: item || {},
    section: sectionData || {},
  }
}
